import { useState, memo, useContext, useEffect } from 'react'
import { Document, Page } from 'react-pdf'
import { useCookies } from 'react-cookie'

import { Close } from '../../assets/icons'
import { KeyboardDoubleArrowRight, Refresh } from '@mui/icons-material'
import { DocumentsContext, SingleDocumentContext } from '../../context'
import { CustomTooltip } from '../ui_new'
import { DOCUMENT_COMMENTS_TIMESTAMPS_COOKIE_NAME, documentCommentSections } from '../../utils'
import { CommentsView } from '.'
const SingleDocumentPreview = ({ data, loading, onPreviewRefresh, onToggle, layout, isPreviewUpToDate = false }) => {
    
    const { revisionModeEnabled, document, documentId, newComments } = useContext(SingleDocumentContext)
    const { templates } = useContext(DocumentsContext)
    const [unreadActivity, setUnreadActivity] = useState([])
    const [docPages, setDocPages] = useState([])

    const [activeTabState, setActiveTab] = useState('preview')
    const [commentsGeneral, setCommentsGeneral] = useState([])
    const [commentSections, setCommentSections] = useState([])

    const [cookies, setCookie] = useCookies()
    const [lastCommentViewTimestamp, setLastCommentViewTimestamp] = useState(0)

    const activeTab = layout === 'public' ? 'comments' : activeTabState

    useEffect(() => {
        const lastCommentTimestampPerDocument = cookies[DOCUMENT_COMMENTS_TIMESTAMPS_COOKIE_NAME] || {}
        const lastCommentTimestampForCurrentDocument = lastCommentTimestampPerDocument[documentId] || 0
        setLastCommentViewTimestamp(lastCommentTimestampForCurrentDocument)
    }, [cookies, documentId])

    useEffect(() => {
        if (!document) return
        const allComments = [...(document?.comments || []), ...(document?.commentsGeneral || [])]
        const newComments = allComments.filter(comment => {
            return comment.date > lastCommentViewTimestamp
        })
        const newSuggestions = (document?.suggestions || []).filter(suggestion => {
            return suggestion.date > lastCommentViewTimestamp
        })
        const unreadActivities = [...newComments, ...newSuggestions]
        unreadActivities.sort((a, b) => b.date - a.date)
        setUnreadActivity(unreadActivities)
    }, [lastCommentViewTimestamp, document])

    useEffect(() => {
        if(activeTab !== 'comments') return
        if(unreadActivity.length === 0) return

        const lastCommentTimestampPerDocument = cookies[DOCUMENT_COMMENTS_TIMESTAMPS_COOKIE_NAME] || {}

        const updatedTimestamps = {
            ...lastCommentTimestampPerDocument,
            [documentId]: Date.now()
        }
        setCookie(DOCUMENT_COMMENTS_TIMESTAMPS_COOKIE_NAME, updatedTimestamps, { path: '/' })
        setLastCommentViewTimestamp(Date.now())
    }, [activeTab, document, cookies, documentId, unreadActivity, setCookie])

    // On document load success
    const handleDocumentLoadSuccess = ({ numPages }) => {
        const arr = []
        for (let i = 1; i < numPages + 1; i++) {
            arr.push(<Page pageNumber={i} width={870} loading="" key={i} />)
        }

        setDocPages(arr)
    }

    // On document preview
    const handleRefreshPreview = () => {
        if (!onPreviewRefresh) return
        onPreviewRefresh()
    }

    const refreshButton = (className) => (
        <button
            className={className}
            onClick={handleRefreshPreview}
            disabled={isPreviewUpToDate}
        >
            <Refresh fontSize='small' />
            Actualiser
        </button>
    )

    const renderRefreshButton = (className) => {
        if (!isPreviewUpToDate) {
            return refreshButton(className)
        }

        return (
            <CustomTooltip content="Aucune modification détectée dans le document, l'aperçu est à jour." className="custom-tooltip-v2--nowrap" position='top-right'>
                <span className="inline-flex">
                    {refreshButton(className)}
                </span>
            </CustomTooltip>
        )
    }

    useEffect(() => {
        if (!document) return
        const commentSections = documentCommentSections({ ...document, comments: [...(document.comments || []), ...newComments] }, templates[document?.template])
        const commentsGeneral = [...(document.commentsGeneral || [])]
        setCommentsGeneral(commentsGeneral)
        setCommentSections(commentSections)
    }, [document, newComments, templates])

    return (
        <div className={`single-document-preview ${layout ? `single-document-preview--${layout}` : ''}`}>
            <div className="single-document-preview__head">
                <div className='flex flex-row justify-between mb-4'>
                    <h2>
                        <span className="text">
                            {revisionModeEnabled || activeTab === 'comments' ? 'Commentaires' : 'Aperçu'}
                        </span>
                    </h2>
                    {layout !== 'public' && 
                        <button className="ml-auto button button--outline-primary-light rounded-sm fold-btn !px-3" onClick={onToggle}>
                            Replier
                            <KeyboardDoubleArrowRight fontSize={'small'} />
                        </button>
                    }
                    <button className="ml-auto fold-btn-mobile" onClick={onToggle}>
                        <Close />
                    </button>
                </div>
                {!revisionModeEnabled && (<>
                    <div className='flex flex-row items-center justify-between flex-wrap gap-2'>
                        
                        <div className='tab-selector'>
                            <div className={`w-1/2 ${activeTab === 'preview' ? 'active' : ''}`} onClick={() => setActiveTab('preview')}>Prévisualisation</div>
                            <div className={`w-1/2 ${activeTab === 'comments' ? 'active' : ''} position-relative`} onClick={() => setActiveTab('comments')}>Commentaires
                                {unreadActivity.length > 0 && (
                                    <span className="badge danger">{unreadActivity.length}</span>
                                )}
                            </div>
                        </div>
                        {activeTab === 'preview' && renderRefreshButton("button button--with-icon button--primary-light rounded-sm refresh-btn")}
                    </div>
                    {activeTab === 'preview' && (
                        renderRefreshButton("button button--primary-light rounded-sm refresh-btn-mobile")
                    )}
                </>)}
            </div>
            <div className="single-document-preview__body">
                {!revisionModeEnabled && activeTab === 'preview' && (
                loading ? (
                    <div className="single-document-preview__body_loader-wrapper">
                        <div className="loader-v2"></div>
                        <div className="mt-5">
                            <span>Création de l'aperçu en cours</span>
                        </div>
                    </div>
                ) : (
                    <Document
                        options={{}}
                        file={data}
                        onLoadSuccess={handleDocumentLoadSuccess}
                        renderMode="canvas"
                        loading="Chargement..."
                        noData="Une erreur est survenue, l'aperçu du document n'a pu être généré"
                    >
                        {docPages}
                    </Document>
                ))}
                {(revisionModeEnabled || activeTab === 'comments') && (
                    [...(commentsGeneral || []), ...(commentSections || [])].length > 0 ? (
                        <>
                            <CommentsView comments={commentsGeneral} isGeneralComments={true} />
                            <CommentsView commentSections={commentSections} canAddComment={true} />
                        </>
                    ) : (
                        <div className='comments--empty'>
                            <span>Aucun commentaire reçu</span>
                        </div>
                    )
                )}
            </div>
        </div>
    )
}

export default memo(SingleDocumentPreview)
